<template>
  <template v-if="currentState === 'ERROR_LOAD'">
    <TAlert kind="red">
      <template #title> {{ t('error.title') }} </template>
      <template #text>
        {{ t('auth.register-optin.errorLoad') }}
      </template>
    </TAlert>
  </template>
  <template v-else-if="currentState === 'SUCCESS'">
    <TModalHeading kind="green" :left="true">
      <template #title>
        {{ t('auth.register-optin.success.title') }}
      </template>

      <template #body>
        {{ t('auth.register-optin.success.text') }}
      </template>
    </TModalHeading>

    <UButton
      color="neutral"
      :ui="mobileFullWidthButton"
      class="mt-6"
      variant="solid"
      size="xl"
      icon="i-heroicons-arrow-top-right-on-square"
      trailing
      external
      :to="routes().home()"
    >
      {{ t('auth.register-optin.success.button') }}
    </UButton>
  </template>

  <template v-else>
    <div>
      <TText size="lg" tag="h1" variant="semibold" classes="mb-4">{{
        t('auth.register-optin.title')
      }}</TText>

      <div v-if="currentState === 'ERROR'" class="my-4">
        <ApiResponseError :api-response-error="apiResponseError" />
      </div>
      <UForm
        ref="form"
        :schema="schema"
        :state="state"
        class="space-y-3"
        @submit="onSubmit"
      >
        <UFormField name="emailAddress">
          <UInput
            v-model="state.emailAddress"
            type="email"
            size="lg"
            :placeholder="t('auth.register-optin.email')"
            :aria-label="t('auth.register-optin.email')"
            :title="t('auth.register-optin.email-title')"
            autocomplete="on"
          />
        </UFormField>
        <UFormField name="emailAddressRepeat">
          <UInput
            v-model="state.emailAddressRepeat"
            type="email"
            size="lg"
            :placeholder="t('auth.register-optin.emailConfirm')"
            :aria-label="t('auth.register-optin.emailConfirm')"
            :title="t('auth.register-optin.emailConfirm-title')"
            onpaste="return false"
            ondrop="return false"
            autocomplete="email"
          />
        </UFormField>
        <UFormField name="firstName">
          <UInput
            v-model="state.firstName"
            size="lg"
            :placeholder="t('auth.register-optin.name')"
            :aria-label="t('auth.register-optin.name')"
            :title="t('auth.register-optin.name-title')"
            autocomplete="on"
          />
        </UFormField>
        <UFormField name="lastName">
          <UInput
            v-model="state.lastName"
            size="lg"
            :placeholder="t('auth.register-optin.surnames')"
            :aria-label="t('auth.register-optin.surnames')"
            :title="t('auth.register-optin.surnames-title')"
            autocomplete="on"
          />
        </UFormField>
        <UFormField name="provinceId">
          <UInputMenu
            v-model="state.provinceId"
            :items="provinces"
            size="lg"
            :placeholder="`${t('auth.register-optin.province')}`"
            :aria-label="t('auth.register-optin.province')"
            :title="t('auth.register-optin.province-title')"
            label-key="name"
            value-key="value"
            autocomplete="on"
          />
        </UFormField>

        <TText size="sm" tag="div" color="text-gray-700" classes="mt-4">
          <i18n-t keypath="auth.register-optin.conditions" scope="global">
            <template #useConditions>
              <a
                class="underline"
                target="_blank"
                :href="routes().useConditions()"
                >{{ t('auth.register-optin.useConditions') }}</a
              >
            </template>
            <template #privacyPolicy>
              <a
                class="underline"
                target="_blank"
                :href="routes().privacyPolicy()"
                >{{ t('auth.register-optin.privacyPolicy') }}</a
              >
            </template>
          </i18n-t>
        </TText>

        <UFormField name="recaptcha">
          <TRecaptcha
            ref="vueRecaptchaRef"
            v-model="state.recaptcha"
            class="mt-6 mb-3"
          />
        </UFormField>

        <div class="flex flex-col items-center">
          <UButton
            color="neutral"
            class="mt-2 mb-6"
            variant="solid"
            size="xl"
            type="submit"
            :disabled="!isValid"
            :loading="currentState === 'SENDING'"
          >
            {{ t('auth.register-optin.submit') }}
          </UButton>
        </div>
      </UForm>
    </div>
  </template>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { browserFetch as fetchOptIn } from '~~/modules/api/rurality/v3/travelers/optIn'
import { paramsSchema } from '~~/modules/api/rurality/v3/travelers/optIn/paramsSchema'
import { scrollToTop } from '~/composables/useAnimations'
import { browserFetch as getProvinces } from '~~/modules/api/rurality/v3/provinces/detail'
import { useToProvinces } from '~/composables/transformers/useToProvinces'
import { signUpOrigin } from '~~/modules/auth/constants'
import { mobileFullWidthButton } from '~~/modules/design-system/utils/nuxt-ui-config/components/button'

const route = useRoute()

interface ChildComponent {
  triggerReset(): void
}

const apiResponseError: Ref<ApiResponseError | null> = ref(null)
const vueRecaptchaRef = ref<ChildComponent | null>(null)

const recaptchaReset = () => {
  vueRecaptchaRef.value?.triggerReset()
}

const form = ref()

const state = reactive({
  firstName: undefined,
  lastName: undefined,
  emailAddress: undefined,
  emailAddressRepeat: undefined,
  provinceId: undefined,
  recaptcha: undefined,
  facebookId: null,
  googleId: null,
  appleId: null,
  signUpOrigin: signUpOrigin.PAGINA_REGISTRO_OPTIN,
  source: Array.isArray(route.query.utm_campaign)
    ? (route.query.utm_campaign[0] ?? undefined)
    : (route.query.utm_campaign ?? undefined),
})

const schemaRaw = paramsSchema.extend({
  firstName: z.string().min(2).max(30),
  lastName: z.string().min(2).max(30),
  provinceId: z
    .number()
    .int()
    .transform(value => (value === undefined ? null : value)),
  emailAddress: z.string().email().min(5).max(254),
})

const schema = useNuxtUiFormRepeat({
  form,
  state,
  schema: schemaRaw,
  parentFieldName: 'emailAddress',
  repeatFieldName: 'emailAddressRepeat',
  message: t('zodI18nCustom.email_repeat'),
})

const states = {
  IDLE: 'IDLE',
  ERROR_LOAD: 'ERROR_LOAD',
  INVALID: 'INVALID',
  VALID: 'VALID',
  SENDING: 'SENDING',
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
}

const currentState: Ref<keyof typeof states> = ref('IDLE')

const isValid = computed(() => {
  return schema.safeParse(state).success
})

async function onSubmit(event: any) {
  const result = schema.safeParse(event.data)
  if (result.success) {
    currentState.value = 'SENDING'

    const parsedData = result.data

    const signUp = await fetchOptIn(parsedData)

    if (signUp.success && signUp.success.data) {
      currentState.value = 'SUCCESS'
    } else {
      apiResponseError.value = signUp.error
      currentState.value = 'ERROR'
      scrollToTop()
      recaptchaReset()
    }
  }
}

// INIT:
const provincesResponse = await getProvinces()

if (provincesResponse.success) {
  currentState.value = 'IDLE'
} else {
  currentState.value = 'ERROR_LOAD'
}

const provincesRaw = provincesResponse.success
  ? provincesResponse.success.data.provinces
  : []

const provinces = useToProvinces().fromRegister(provincesRaw)
</script>
